import React, { useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { useDispatch, useSelector } from "react-redux";
import styles from "./multi-line-editor.module.scss";
import { useI18N } from "@src/language/language-context";
import {
  GenericWidgetSemantics,
  RoleBinding,
  Widget,
  WidgetSemantics,
} from "@src/types/dashboards";
import { MachineNameAndSourceId } from "@common/types/server-list-types";
import { getUnitById } from "@src/cloud-connect/units/utils/get-unit-by-id";
import { unitOptionLabelOf } from "@src/cloud-connect/units/utils/unit-option-label-of";
import type { UnitDefinition } from "@src/types/units";
import {
  selectUnitsById,
  selectUnitsStatus,
  unitsFailed,
  unitsLoaded,
  unitsRequested,
} from "@src/reducers/units-slice";
import { getAllSourcesListUseCase } from "@src/cloud-connect/client-configuration/use-cases/get-all-sources-list-use-case";
import { getActiveTimeseriesUseCase } from "@src/cloud-connect/timeseries/use-cases/get-active-timeseries-use-case";
import { getUnitsUseCase } from "@src/cloud-connect/timeseries/use-cases/get-units-use-case";
import {
  buildMultiLineQuery,
  getWidgetWindow,
} from "@src/components/organisms/widgets/widget-query";
import {
  AGGREGATED_POLLING_LABELS,
  durationToIso,
  isoToDurationMs,
  LINE_CHART_DEFAULT_DURATION_MS,
  LINE_CHART_DURATIONS_MS,
  LINE_CHART_GRANULARITIES,
} from "@src/components/organisms/widgets/constants";
import {
  getNumericNodeUnit,
  toNumericNodeOptions,
} from "@src/components/organisms/widgets/node-options";
import {
  buildScaleTransformsForEdits,
  scaleForField,
} from "@src/components/organisms/widgets/utils/value-scaling";
import { parseUserNumber } from "@src/components/organisms/widgets/utils/parse-user-number";
import { axisBoundsInputError } from "@src/components/organisms/widgets/axis-bounds";
import { buildDurationOptions } from "@src/components/organisms/widgets/utils/build-duration-options";
import { findDurationOption } from "@src/components/organisms/widgets/utils/find-duration-options";
import { MULTI_LINE_DEFAULT_COLORS } from "@src/components/organisms/widgets/multi-line/build-multi-line-option";
import { ScaleInput } from "@src/components/atoms/scale-input/scale-input";
import { DashboardEditWidgetDrawerDropdown } from "@src/components/atoms/dashboard-edit-widget-drawer-dropdown/dashboard-edit-widget-drawer-dropdown";
import { DatePicker } from "@src/components/atoms/date-picker/date-picker";
import { CheckBoxWithText } from "@src/components/atoms/check-box-with-text/check-box-with-text";
import {
  ColorSwatchPicker,
  pickPaletteHex,
  TREND_LINE_PALETTE,
} from "@src/components/atoms/color-swatch-picker/color-swatch-picker";
import { ThresholdFields } from "@src/components/molecules/threshold-fields/threshold-fields";
import { ManualYRangeFields } from "@src/components/molecules/manual-y-range-fields/manual-y-range-fields";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";
import { ToolTip } from "@src/components/atoms/tooltip/tooltip";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { Button } from "@src/components/atoms/buttons/button/button";
import Spinner from "@src/components/atoms/spinner/spinner";
import infoIconFilled from "@src/assets/svg/info-icon-filled.svg";
import closeIcon from "@src/assets/svg/close-icon.svg";
import plusCircleIcon from "@src/assets/svg/plus-circle-icon.svg";
import { useSaveWidget } from "../hooks/use-save-widget";

const cssClassName = "multi-line-editor";

/** One row of the series list: a (machine, node) pair, its colour and its
 *  optional scale factor. Floor 2 rows, ceiling 5 — the BE declares
 *  `min_roles: 2, max_roles: 5` on the encoding. */
interface EditorLine {
  machine: MachineNameAndSourceId | null;
  node: MachineNameAndSourceId | null;
  color: string | null;
  scale: string;
}

export interface MultiLineEditorProps {
  widget: Widget;
  isPendingWidget: boolean;
  onClose: () => void;
}

/**
 * Editor for a `multi-line` widget: one (machine, node, colour) row per series
 * plus the chart-wide unit, the aggregation bucket, the window (a rolling
 * duration or an absolute timeframe), the warning / critical thresholds, the
 * aggregated min/max display toggles, the optional manual Y range and the
 * per-series scale factors. Owns its own state, its own fetching and its own
 * save.
 */
export const MultiLineEditor = ({
  widget,
  isPendingWidget,
  onClose,
}: MultiLineEditorProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const units = useSelector(selectUnitsById);
  const unitsStatus = useSelector(selectUnitsStatus);
  const isLoadingUnits = unitsStatus === "idle" || unitsStatus === "loading";

  const savedSemantics = widget.semantics as Partial<WidgetSemantics> & {
    min_value?: number | null;
    max_value?: number | null;
    thresholds?: { warning?: number | null; critical?: number | null } | null;
  };
  const savedBounds = widget.semantics as Partial<GenericWidgetSemantics>;
  const savedWindow = getWidgetWindow(widget.query);
  const savedDurationMs = isoToDurationMs(savedWindow?.duration);
  // A stored duration wins over a stored timeframe — the two are mutually
  // exclusive and the payload only ever carries one of them.
  const savedFrom = savedDurationMs == null ? savedWindow?.from ?? null : null;
  const savedTo = savedDurationMs == null ? savedWindow?.to ?? null : null;
  // Pending widgets ignore the template's unit; an existing one keeps it.
  const savedUnitId = isPendingWidget
    ? null
    : widget.presentation?.unit ?? null;

  const [title, setTitle] = useState(widget.title);
  const [frequency, setFrequency] = useState<string | null>(
    widget.query?.sampling?.frequency ?? "hourly",
  );
  const [isTimeframe, setIsTimeframe] = useState(
    savedDurationMs == null && (savedFrom != null || savedTo != null),
  );
  const [fromDate, setFromDate] = useState<string | null>(savedFrom);
  const [toDate, setToDate] = useState<string | null>(savedTo);
  const [durationMs, setDurationMs] = useState<number | null>(
    savedDurationMs ?? LINE_CHART_DEFAULT_DURATION_MS,
  );
  const [lines, setLines] = useState<EditorLine[]>(() => {
    const bindings = widget.query?.bindings ?? [];
    const minimumRows = Math.max(2, bindings.length);
    const roleMap = widget.encoding?.roles ?? {};
    // Colours already handed out, so a fresh row never repeats one.
    const used: string[] = [];
    return Array.from({ length: minimumRows }, (_, idx) => {
      const binding = bindings[idx];
      const role = roleMap[`value_${idx}`];
      const roleColor = role && !Array.isArray(role) ? role.color : undefined;
      if (binding && binding.source && binding.address) {
        // Bound row: a saved colour wins, else the chart's own positional
        // fallback — so an existing widget keeps the colour it renders today.
        const color = roleColor ?? MULTI_LINE_DEFAULT_COLORS[idx] ?? null;
        if (color) used.push(color);
        return {
          machine: { name: binding.source, source_id: binding.source },
          node: { name: binding.address, source_id: binding.address },
          color,
          scale: String(
            scaleForField(`value_${idx}`, widget.encoding?.transforms),
          ),
        };
      }
      // Empty row: nothing to preserve, so take a colour from the palette
      // (a positional one might not be in the picker's list at all).
      const color = pickPaletteHex(used);
      used.push(color);
      return { machine: null, node: null, color, scale: "1" };
    });
  });
  const [unit, setUnit] = useState<UnitDefinition | null>(() =>
    getUnitById(units, savedUnitId),
  );
  // Auto-pick keeps re-evaluating on every row change until save; a manual
  // pick (handleUnitSelection) is the only thing that turns it off.
  const [shouldAutoPickUnit, setShouldAutoPickUnit] = useState(
    savedUnitId == null,
  );
  const [min, setMin] = useState(
    savedSemantics.min_value != null ? String(savedSemantics.min_value) : "",
  );
  const [max, setMax] = useState(
    savedSemantics.max_value != null ? String(savedSemantics.max_value) : "",
  );
  const [manualYRange, setManualYRange] = useState(
    savedSemantics.min_value != null && savedSemantics.max_value != null,
  );
  const [minEnabled, setMinEnabled] = useState(savedBounds.show_min === true);
  const [maxEnabled, setMaxEnabled] = useState(savedBounds.show_max === true);
  const [warningEnabled, setWarningEnabled] = useState(
    savedSemantics.thresholds?.warning != null,
  );
  const [warningValue, setWarningValue] = useState(
    savedSemantics.thresholds?.warning != null
      ? String(savedSemantics.thresholds.warning)
      : "",
  );
  const [criticalEnabled, setCriticalEnabled] = useState(
    savedSemantics.thresholds?.critical != null,
  );
  const [criticalValue, setCriticalValue] = useState(
    savedSemantics.thresholds?.critical != null
      ? String(savedSemantics.thresholds.critical)
      : "",
  );
  const [machineOptions, setMachineOptions] = useState<
    MachineNameAndSourceId[]
  >([]);
  // Per-machine node caches. Each row picks its own machine, so one shared
  // node list is not enough. Loading is inferred by
  // `nodesByMachine[id] === undefined`.
  const [nodesByMachine, setNodesByMachine] = useState<
    Record<string, MachineNameAndSourceId[]>
  >({});
  // Per-row unit cache, indexed by machine id then node id. The consensus
  // effect below reads it: when every row agrees on a unit the field is
  // filled, when they disagree it is cleared.
  const [nodeUnitsByMachine, setNodeUnitsByMachine] = useState<
    Record<string, Record<string, number | null>>
  >({});
  const [isLoadingMachines, setIsLoadingMachines] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);

  const texts = {
    saveButton: i18n("modules.multiLine.saveButton"),
    cancelButton: i18n("modules.multiLine.cancelButton"),
    titleLabel: i18n("modules.multiLine.titleLabel"),
    titleInputPlaceholder: i18n("modules.multiLine.titleInputPlaceholder"),
    machineLabel: i18n("modules.multiLine.machineLabel"),
    noMachines: i18n("modules.multiLine.noMachines"),
    machinesPlaceholder: i18n("modules.multiLine.machinesPlaceholder"),
    nodeLabel: i18n("modules.multiLine.nodeLabel"),
    noNodes: i18n("modules.multiLine.noNodes"),
    nodesPlaceholder: i18n("modules.multiLine.nodesPlaceholder"),
    unitLabel: i18n("modules.multiLine.unitLabel"),
    noUnits: i18n("modules.multiLine.noUnits"),
    unitsPlaceholder: i18n("modules.multiLine.unitsPlaceholder"),
    multiLineSeriesLabel: i18n("modules.multiLine.multiLineSeriesLabel"),
    addLine: i18n("modules.multiLine.addLine"),
    removeLineAlt: i18n("modules.multiLine.removeLineAlt"),
    colorLabel: i18n("modules.multiLine.colorLabel"),
    frequencyLabel: i18n("modules.multiLine.frequencyLabel"),
    noFrequencies: i18n("modules.multiLine.noFrequencies"),
    granularityPlaceholder: i18n("modules.multiLine.granularityPlaceholder"),
    timeRangeLabel: i18n("modules.multiLine.timeRangeLabel"),
    lookbackLabel: i18n("modules.multiLine.lookbackLabel"),
    timeframeLabel: i18n("modules.multiLine.timeframeLabel"),
    startLabel: i18n("modules.multiLine.startLabel"),
    endLabel: i18n("modules.multiLine.endLabel"),
    noLookbacks: i18n("modules.multiLine.noLookbacks"),
    lookbackPlaceholder: i18n("modules.multiLine.lookbackPlaceholder"),
    pollingIntervalLabel: i18n("modules.multiLine.pollingIntervalLabel"),
    pollingInfoText: i18n("modules.multiLine.pollingInfoText"),
    thresholdLabel: i18n("modules.multiLine.thresholdLabel"),
    warningLabel: i18n("modules.multiLine.warningLabel"),
    criticalLabel: i18n("modules.multiLine.criticalLabel"),
    aggregateBoundsLabel: i18n("modules.multiLine.aggregateBoundsLabel"),
    showMinLabel: i18n("modules.multiLine.showMinLabel"),
    showMaxLabel: i18n("modules.multiLine.showMaxLabel"),
    manualYRangeLabel: i18n("modules.multiLine.manualYRangeLabel"),
    yMinLabel: i18n("modules.multiLine.yMinLabel"),
    yMaxLabel: i18n("modules.multiLine.yMaxLabel"),
    resetYRangeLabel: i18n("modules.multiLine.resetYRangeLabel"),
    titleRequired: i18n("modules.multiLine.titleRequired"),
    timeframeRequired: i18n("modules.multiLine.timeframeRequired"),
    durationRequired: i18n("modules.multiLine.durationRequired"),
    frequencyRequired: i18n("modules.multiLine.frequencyRequired"),
    lineFieldsEmpty: i18n("modules.multiLine.lineFieldsEmpty"),
    yAxisBoundsRequired: i18n("modules.multiLine.yAxisBoundsRequired"),
    yAxisRangeInvalid: i18n("modules.multiLine.yAxisRangeInvalid"),
    warningValueRequired: i18n("modules.multiLine.warningValueRequired"),
    criticalValueRequired: i18n("modules.multiLine.criticalValueRequired"),
    thresholdOrderInvalid: i18n("modules.multiLine.thresholdOrderInvalid"),
    scaleInvalid: i18n("modules.multiLine.scaleInvalid"),
  };

  // Multi-line is unconditionally aggregated: the polling cadence follows the
  // bucket size instead of being picked, so only the bucket needs loading.
  const linesMachinesKey = useMemo(
    () => lines.map((line) => line.machine?.source_id || "_").join("|"),
    [lines],
  );
  useEffect(() => {
    const machineIds = Array.from(
      new Set(
        linesMachinesKey
          .split("|")
          .filter((id): id is string => Boolean(id) && id !== "_"),
      ),
    );
    if (machineIds.length === 0) return;

    // One timeseries call per machine. Cache writes are idempotent (bail if
    // the machine is already cached), so a re-fired key keeps the cached
    // nodes and only re-issues the request.
    let cancelled = false;
    Promise.all(
      machineIds.map(async (id) => {
        const result = await getActiveTimeseriesUseCase(id);
        return { id, result };
      }),
    ).then((results) => {
      if (cancelled) return;
      setNodesByMachine((prev) => {
        const next = { ...prev };
        for (const { id, result } of results) {
          if (next[id] !== undefined) continue;
          next[id] =
            result.success && result.data
              ? toNumericNodeOptions(result.data.timeseries)
              : [];
        }
        return next;
      });
      // Same response, second view: the per-machine unit references the
      // consensus effect below needs, so no second fetch.
      setNodeUnitsByMachine((prev) => {
        const next = { ...prev };
        for (const { id, result } of results) {
          if (next[id] !== undefined) continue;
          next[id] =
            result.success && result.data
              ? Object.fromEntries(
                  Object.values(result.data.timeseries)
                    .filter((t) => t.data_type === "numeric")
                    .map((t) => [
                      t.source,
                      getNumericNodeUnit(result.data!.timeseries, t.source),
                    ]),
                )
              : {};
        }
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [linesMachinesKey]);

  // Once the machines, the unit catalog and every bound machine's nodes are
  // in, reveal the form — the same wait the drawer used to do before handing
  // the widget over.
  useEffect(() => {
    if (!isInitializing) return;
    const rowsLoaded = lines.every(
      (line) =>
        !line.machine || nodesByMachine[line.machine.source_id] !== undefined,
    );
    if (!isLoadingMachines && !isLoadingUnits && rowsLoaded) {
      setIsInitializing(false);
    }
  }, [
    isLoadingMachines,
    isLoadingUnits,
    nodesByMachine,
    lines,
    isInitializing,
  ]);

  // Load machines once.
  useEffect(() => {
    setIsLoadingMachines(true);
    getAllSourcesListUseCase().then((result) => {
      if (result.success && result.data) {
        setMachineOptions(
          Object.values(result.data).map((s) => ({
            name: s.name,
            source_id: s.source_id,
          })),
        );
      } else {
        setMachineOptions([]);
        console.error("Failed to load machines:", result.error);
      }
      setIsLoadingMachines(false);
    });
  }, []);

  // Units come from the global slice (loaded at dashboards-load). Lazy
  // fallback: if the slice was never attempted, fetch once so the dropdown
  // isn't empty.
  const unitsAttempted = useRef(false);
  useEffect(() => {
    if (unitsStatus !== "idle" || unitsAttempted.current) return;
    unitsAttempted.current = true;
    dispatch(unitsRequested());
    getUnitsUseCase().then((result) => {
      if (result.success && result.data) {
        dispatch(unitsLoaded(result.data));
      } else {
        dispatch(unitsFailed(result.error ?? "Failed to fetch units"));
      }
    });
  }, [unitsStatus, dispatch]);

  // The sources list loads asynchronously, so the seed hands each row its
  // machine id and this effect swaps in the real name once the options
  // arrive. Returns the previous array reference when nothing changed so
  // React doesn't re-render.
  useEffect(() => {
    if (machineOptions.length === 0) return;
    setLines((prev) => {
      let mutated = false;
      const next = prev.map((line) => {
        if (!line.machine) return line;
        const match = machineOptions.find(
          (m) => m.source_id === line.machine!.source_id,
        );
        if (!match) return line;
        if (line.machine.name === match.name) return line;
        mutated = true;
        return {
          ...line,
          machine: { name: match.name, source_id: match.source_id },
        };
      });
      return mutated ? next : prev;
    });
  }, [machineOptions]);

  // Same raw-id → human-name swap for the nodes, against the per-machine
  // cache filled above. A row whose machine has not answered yet is skipped;
  // the effect fires again when the cache populates.
  useEffect(() => {
    setLines((prev) => {
      let mutated = false;
      const next = prev.map((line) => {
        if (!line.machine || !line.node) return line;
        const options = nodesByMachine[line.machine.source_id];
        if (!options || options.length === 0) return line;
        const match = options.find((n) => n.source_id === line.node!.source_id);
        if (!match) return line;
        if (line.node.name === match.name) return line;
        mutated = true;
        return {
          ...line,
          node: { name: match.name, source_id: match.source_id },
        };
      });
      return mutated ? next : prev;
    });
  }, [nodesByMachine]);

  // Consensus unit: the chart-wide unit comes from the rows that are actually
  // bound. When they all name the same unit the field is filled; when they
  // disagree, or a node names no unit, it is cleared. A row still missing a
  // machine or node is ignored, and a bound row whose machine has not answered
  // yet bails — a hiccup between machines must not clear the field.
  useEffect(() => {
    if (!shouldAutoPickUnit) return;
    const refs = lines
      .filter((line) => line.machine && line.node)
      .map(
        (line) =>
          nodeUnitsByMachine[line.machine!.source_id]?.[line.node!.source_id],
      );
    if (refs.length === 0) return;
    if (refs.some((ref) => ref === undefined)) return;
    const first = refs[0];
    if (first == null || refs.some((ref) => ref !== first)) {
      setUnit(null);
      return;
    }
    setUnit(findUnitByReference(first) ?? null);
  }, [shouldAutoPickUnit, lines, nodeUnitsByMachine, units]);

  // Restore the saved unit once the catalog is available (the global load may
  // still be in flight when the editor opens).
  useEffect(() => {
    if (isPendingWidget) return;
    const id = widget.presentation?.unit;
    if (id == null) return;
    const match = getUnitById(units, id);
    if (match) setUnit(match);
  }, [units, widget.presentation?.unit, isPendingWidget]);

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      {
        machine: null,
        node: null,
        // A colour from the picker's palette, avoiding the ones already used.
        color: pickPaletteHex(prev.map((line) => line.color ?? "")),
        scale: "1",
      },
    ]);
  };

  const removeLine = (idx: number) => {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateLine = (idx: number, patch: Partial<EditorLine>) => {
    setLines((prev) =>
      prev.map((line, i) => (i === idx ? { ...line, ...patch } : line)),
    );
  };

  const updateLineMachine = (idx: number, m: MachineNameAndSourceId | null) => {
    // Switching machine clears the bound node — the previous node id almost
    // certainly doesn't exist on the new machine. Releasing the manual unit
    // lock lets the consensus recompute from the rows that are still bound.
    setShouldAutoPickUnit(true);
    setLines((prev) =>
      prev.map((line, i) =>
        i === idx ? { ...line, machine: m, node: null } : line,
      ),
    );
  };

  const updateLineNode = (idx: number, node: MachineNameAndSourceId | null) => {
    // A new node re-derives the chart unit; a manual pick must not persist.
    setShouldAutoPickUnit(true);
    updateLine(idx, { node });
  };

  const unitOptions = Object.entries(units).map(([key, value]) => {
    const text =
      value.unit_text?.eng ?? Object.values(value.unit_text ?? {})[0];
    const label = unitOptionLabelOf(text, value.unit_id);
    return {
      name: label,
      searchText: [
        value.unit_id,
        value.external_reference,
        text?.name,
        text?.symbol,
        text?.description,
      ]
        .filter(Boolean)
        .join(" "),
      source_id: key,
    };
  });
  const selectedUnitEntry = unit
    ? Object.entries(units).find(
        ([key, candidate]) =>
          key === unit.unit_id || candidate.unit_id === unit.unit_id,
      )
    : undefined;
  const selectedUnitOption = unit
    ? {
        name: unitOptionLabelOf(unit.unit_text?.eng, unit.unit_id),
        source_id: selectedUnitEntry?.[0] ?? unit.unit_id,
      }
    : null;
  const findUnitByReference = (reference: number | string | null) =>
    reference == null
      ? undefined
      : Object.values(units).find(
          (u) => u.external_reference === String(reference),
        );

  // Picking a unit and clearing the field both stop the auto-pick; only
  // picking a node starts it again. Auto-pick writes above do NOT change the
  // flag — writing isn't picking.
  const handleUnitSelection = (selected: MachineNameAndSourceId | null) => {
    setUnit(
      selected
        ? units[selected.source_id] ??
            Object.values(units).find(
              (candidate) => candidate.unit_id === selected.source_id,
            ) ??
            null
        : null,
    );
    setShouldAutoPickUnit(false);
  };

  const validate = (): string | null => {
    if (title.trim() === "") return texts.titleRequired;
    if (frequency == null || frequency === "") {
      return texts.frequencyRequired;
    }
    // The window is answered either by an absolute timeframe or by a rolling
    // duration preset. A cleared duration answers nothing.
    if (isTimeframe) {
      if (!fromDate || !toDate) return texts.timeframeRequired;
    } else if (durationMs == null) {
      return texts.durationRequired;
    }
    // Warn on save when any row's machine OR node is empty so the user can
    // either close it (X) or fill it. Save is NOT blocked on fewer than two
    // rows — the BE enforces `min_roles=2` server-side and we surface that 422
    // to the user as a learning signal.
    for (const line of lines) {
      if (!line.machine || !line.node) return texts.lineFieldsEmpty;
      if (line.scale.trim() !== "" && parseUserNumber(line.scale) === null) {
        return texts.scaleInvalid;
      }
    }
    // Manual Y range: both ends required, and min must stay below max.
    if (manualYRange) {
      const boundsError = axisBoundsInputError(min, max);
      if (boundsError === "required") return texts.yAxisBoundsRequired;
      if (boundsError === "invalid") return texts.yAxisRangeInvalid;
    }
    if (warningEnabled && parseUserNumber(warningValue) == null) {
      return texts.warningValueRequired;
    }
    if (criticalEnabled && parseUserNumber(criticalValue) == null) {
      return texts.criticalValueRequired;
    }
    if (
      warningEnabled &&
      criticalEnabled &&
      parseUserNumber(warningValue) != null &&
      parseUserNumber(criticalValue) != null &&
      (parseUserNumber(warningValue) as number) >
        (parseUserNumber(criticalValue) as number)
    ) {
      return texts.thresholdOrderInvalid;
    }
    return null;
  };

  const { save, isSaving } = useSaveWidget(widget, isPendingWidget, onClose);

  const handleSave = () => {
    // Each enabled threshold contributes its key; disabled/removed thresholds
    // are dropped. Both off → null.
    const thresholds: { warning?: number; critical?: number } = {};
    if (warningEnabled && parseUserNumber(warningValue) != null) {
      thresholds.warning = parseUserNumber(warningValue) as number;
    }
    if (criticalEnabled && parseUserNumber(criticalValue) != null) {
      thresholds.critical = parseUserNumber(criticalValue) as number;
    }
    const thresholdsOut =
      Object.keys(thresholds).length > 0 ? thresholds : null;

    // Validation already gates empty rows, but incomplete rows are dropped
    // defensively (e.g. a legacy widget whose binding never resolved to a
    // machine we still have). Each surviving row is renumbered 0..N-1 so the
    // binding indices stay contiguous against `encoding.roles.value_X`.
    const completedLines = lines.filter((line) => line.machine && line.node);

    const updated: Widget = {
      ...widget,
      title: title.trim() || widget.title,
      image_url: widget.image_url ?? null,
      query: buildMultiLineQuery({
        bindings: completedLines.map((line, idx) => ({
          field: `value_${idx}`,
          source: line.machine!.source_id,
          address: line.node!.source_id,
        })),
        // Multi-line is always aggregated; default to "hourly" if somehow
        // missing.
        frequency: frequency ?? "hourly",
        durationIso: isTimeframe
          ? null
          : durationMs != null
          ? durationToIso(durationMs)
          : null,
        from: isTimeframe ? fromDate : null,
        to: isTimeframe ? toDate : null,
      }),
      semantics: {
        thresholds: thresholdsOut,
        show_min: minEnabled || undefined,
        show_max: maxEnabled || undefined,
        min_value: manualYRange ? parseUserNumber(min) : null,
        max_value: manualYRange ? parseUserNumber(max) : null,
      } as WidgetSemantics,
      encoding: {
        ...widget.encoding,
        roles: lines.reduce<Record<string, RoleBinding>>((acc, line, idx) => {
          acc[`value_${idx}`] = {
            field: `value_${idx}`,
            label: `Series ${idx + 1}`,
            color: line.color ?? MULTI_LINE_DEFAULT_COLORS[idx] ?? null,
            order: idx,
          };
          return acc;
        }, {}),
        min_roles: 2,
        max_roles: 5,
        ...buildScaleTransformsForEdits(
          widget.encoding?.transforms,
          widget.query?.bindings?.length ?? 0,
          lines,
        ),
      },
      presentation: {
        ...widget.presentation,
        unit: unit?.unit_id ?? null,
      },
    };

    save(updated, { validate, imageFile: null });
  };

  if (isInitializing) {
    return (
      <div className={styles[`${cssClassName}__loading`]}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles[`${cssClassName}__outer-wrapper`]}>
      <Input2025
        inputPlaceHolder={texts.titleInputPlaceholder}
        labelText={texts.titleLabel}
        inputValue={title}
        onChangeFunc={(e) => setTitle(e.target.value)}
        inputVariant="dark-background"
      />

      <span className={styles[`${cssClassName}__toggle-label`]}>
        {texts.multiLineSeriesLabel}
      </span>
      {lines.map((line, idx) => (
        <div key={idx} className={styles[`${cssClassName}__line`]}>
          <DashboardEditWidgetDrawerDropdown
            options={machineOptions}
            selectedOption={line.machine}
            onSelectOption={(s) => updateLineMachine(idx, s)}
            labelText={texts.machineLabel}
            noOptionsText={texts.noMachines}
            placeholderText={texts.machinesPlaceholder}
            isLoading={isLoadingMachines}
          />
          <DashboardEditWidgetDrawerDropdown
            options={
              line.machine ? nodesByMachine[line.machine.source_id] ?? [] : []
            }
            selectedOption={line.node}
            onSelectOption={(s) => updateLineNode(idx, s)}
            labelText={texts.nodeLabel}
            noOptionsText={texts.noNodes}
            placeholderText={texts.nodesPlaceholder}
            isLoading={
              line.machine
                ? nodesByMachine[line.machine.source_id] === undefined
                : false
            }
          />
          <ColorSwatchPicker
            label={texts.colorLabel}
            value={line.color}
            swatches={TREND_LINE_PALETTE}
            onChange={(c) => updateLine(idx, { color: c })}
          />
          <ScaleInput
            value={line.scale}
            onChange={(raw) => updateLine(idx, { scale: raw })}
          />
          {lines.length > 2 && (
            <button
              type="button"
              className={styles[`${cssClassName}__line-remove`]}
              onClick={() => removeLine(idx)}
              aria-label={texts.removeLineAlt}
            >
              <img src={closeIcon} alt="" />
            </button>
          )}
        </div>
      ))}
      {lines.length < 5 && (
        <button
          type="button"
          className={styles[`${cssClassName}__add-line`]}
          onClick={addLine}
          aria-label={texts.addLine}
        >
          <img
            src={plusCircleIcon}
            alt=""
            className={styles[`${cssClassName}__plus-icon`]}
          />
          {texts.addLine}
        </button>
      )}

      {/* Chart-wide unit, shared across all rows. */}
      <div className={styles[`${cssClassName}__field`]}>
        <DashboardEditWidgetDrawerDropdown
          options={unitOptions}
          selectedOption={selectedUnitOption}
          onSelectOption={handleUnitSelection}
          labelText={texts.unitLabel}
          noOptionsText={texts.noUnits}
          placeholderText={texts.unitsPlaceholder}
          isLoading={isLoadingUnits}
        />
      </div>

      {/* Aggregation bucket. `raw` is filtered out: multi-line is always
          aggregated, so the dropdown only offers buckets. */}
      <div className={styles[`${cssClassName}__field`]}>
        <DashboardEditWidgetDrawerDropdown
          options={LINE_CHART_GRANULARITIES.filter((g) => g !== "raw").map(
            (g) => ({ name: g, source_id: g }),
          )}
          selectedOption={
            frequency ? { name: frequency, source_id: frequency } : null
          }
          onSelectOption={(s) => setFrequency(s?.source_id ?? null)}
          labelText={texts.frequencyLabel}
          noOptionsText={texts.noFrequencies}
          placeholderText={texts.granularityPlaceholder}
          isLoading={false}
        />
      </div>

      {/* Duration OR absolute timeframe. */}
      <div>
        <span className={styles[`${cssClassName}__toggle-label`]}>
          {texts.timeRangeLabel}
        </span>
        <div className={styles[`${cssClassName}__toggle`]}>
          <button
            type="button"
            className={cn(styles[`${cssClassName}__toggle-button`], {
              [styles[`${cssClassName}__toggle-button--active`]]: !isTimeframe,
            })}
            onClick={() => setIsTimeframe(false)}
          >
            {texts.lookbackLabel}
          </button>
          <button
            type="button"
            className={cn(styles[`${cssClassName}__toggle-button`], {
              [styles[`${cssClassName}__toggle-button--active`]]: isTimeframe,
            })}
            onClick={() => setIsTimeframe(true)}
          >
            {texts.timeframeLabel}
          </button>
        </div>
      </div>
      {isTimeframe ? (
        <div className={styles[`${cssClassName}__row`]}>
          <DatePicker
            selectedDate={fromDate}
            onDateChange={setFromDate}
            placeholder={texts.startLabel}
          />
          <DatePicker
            selectedDate={toDate}
            onDateChange={setToDate}
            placeholder={texts.endLabel}
          />
        </div>
      ) : (
        <div className={styles[`${cssClassName}__field`]}>
          <DashboardEditWidgetDrawerDropdown
            options={buildDurationOptions(LINE_CHART_DURATIONS_MS, i18n)}
            selectedOption={
              durationMs != null
                ? findDurationOption(LINE_CHART_DURATIONS_MS, durationMs, i18n)
                : null
            }
            onSelectOption={(s) =>
              setDurationMs(s ? Number(s.source_id) : null)
            }
            labelText={texts.lookbackLabel}
            noOptionsText={texts.noLookbacks}
            placeholderText={texts.lookbackPlaceholder}
            isLoading={false}
          />
        </div>
      )}

      {/* Polling cadence: multi-line is always aggregated, so it is locked to
          the bucket cadence and explained by a tooltip instead of picked. */}
      <div className={styles[`${cssClassName}__field`]}>
        <span className={styles[`${cssClassName}__polling-label`]}>
          {texts.pollingIntervalLabel}
          <ToolTip content={texts.pollingInfoText} position="top">
            <img
              src={infoIconFilled}
              alt=""
              className={styles[`${cssClassName}__polling-info-icon`]}
            />
          </ToolTip>
        </span>
        <div className={styles[`${cssClassName}__polling-locked`]}>
          {AGGREGATED_POLLING_LABELS[frequency ?? ""] ?? frequency}
        </div>
      </div>

      {/* Each threshold is checkbox-gated: the value input shows only when
          enabled and is required then. Both off → thresholds null. Distinct
          from the aggregated bounds below, which toggle the min/max series. */}
      <ThresholdFields
        texts={{
          thresholdLabel: texts.thresholdLabel,
          warningLabel: texts.warningLabel,
          criticalLabel: texts.criticalLabel,
        }}
        warningEnabled={warningEnabled}
        warningValue={warningValue}
        onWarningEnabledChange={setWarningEnabled}
        onWarningValueChange={setWarningValue}
        criticalEnabled={criticalEnabled}
        criticalValue={criticalValue}
        onCriticalEnabledChange={setCriticalEnabled}
        onCriticalValueChange={setCriticalValue}
      />

      <fieldset className={styles[`${cssClassName}__thresholds`]}>
        <legend className={styles[`${cssClassName}__field-label`]}>
          {texts.aggregateBoundsLabel}
        </legend>
        <div className={styles[`${cssClassName}__threshold-row`]}>
          <CheckBoxWithText
            text={texts.showMinLabel}
            checkboxState={minEnabled}
            handleCheckBoxClick={() => setMinEnabled((prev) => !prev)}
          />
        </div>
        <div className={styles[`${cssClassName}__threshold-row`]}>
          <CheckBoxWithText
            text={texts.showMaxLabel}
            checkboxState={maxEnabled}
            handleCheckBoxClick={() => setMaxEnabled((prev) => !prev)}
          />
        </div>
      </fieldset>

      <ManualYRangeFields
        texts={{
          manualYRangeLabel: texts.manualYRangeLabel,
          yMinLabel: texts.yMinLabel,
          yMaxLabel: texts.yMaxLabel,
          resetYRangeLabel: texts.resetYRangeLabel,
        }}
        enabled={manualYRange}
        min={min}
        max={max}
        onEnabledChange={setManualYRange}
        onMinChange={setMin}
        onMaxChange={setMax}
        onReset={() => {
          setMin("");
          setMax("");
          setManualYRange(false);
        }}
      />

      <Spacer16 />
      <FlexRowContainer>
        <ButtonWithLoader
          fun={handleSave}
          text={texts.saveButton}
          loading={isSaving}
        />
        <Button
          fun={onClose}
          text={texts.cancelButton}
          variant="secondary"
          // Cancel must NOT fire mid-save: it would dispatch
          // removePendingWidget while POST/PUT are still in flight and leak a
          // server-side orphan.
          disabled={isSaving}
        />
      </FlexRowContainer>
    </div>
  );
};
